/**
 * Behaviour for the landing page. Loaded from layout/theme.liquid alongside
 * top.css, and only on the index template.
 */

/**
 * <lcl-slider> — a looping, dragging, self-advancing row.
 *
 * It advances by scrolling the container, never by transforming it, so the
 * platform keeps ownership of momentum, snapping and the scrollbar. Swipe,
 * trackpad and keyboard work without a line of code, and the markup degrades to
 * a plain scrollable row if this file never runs. The drag handler exists
 * purely because a mouse cannot swipe.
 *
 * The loop is made of clones for the same reason: a copy of the whole set sits
 * either side of the real one, and once the reader settles on a copy the scroll
 * position jumps a full set back without animating. The view is identical at
 * both positions, so the jump cannot be seen.
 *
 * Expected markup:
 *
 *   <lcl-slider data-interval="5000">
 *     <div data-slider-track tabindex="0" role="group" aria-label="…">
 *       <ul data-slider-list>
 *         <li data-slider-slide>…</li>
 *       </ul>
 *     </div>
 *   </lcl-slider>
 */
class LclSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-slider-track]');
    this.list = this.querySelector('[data-slider-list]');
    const originals = Array.from(this.querySelectorAll('[data-slider-slide]'));

    if (!this.track || !this.list || originals.length < 2) return;

    this.count = originals.length;
    this.interval = Number(this.dataset.interval) || 5000;
    this.paused = false;
    this.dragging = false;
    this.silent = false;
    this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.buildLoop(originals);
    this.slides = Array.from(this.list.children);

    this.handleScroll = this.handleScroll.bind(this);
    this.handleVisibility = this.handleVisibility.bind(this);
    this.handleDragStart = this.handleDragStart.bind(this);
    this.handleDragMove = this.handleDragMove.bind(this);
    this.handleDragEnd = this.handleDragEnd.bind(this);

    ['pointerenter', 'focusin'].forEach((event) => {
      this.addEventListener(event, () => {
        this.paused = true;
      });
    });

    ['pointerleave', 'focusout'].forEach((event) => {
      this.addEventListener(event, () => {
        this.paused = false;
      });
    });

    // Touch already drags the container natively; this is only for a mouse.
    this.track.addEventListener('pointerdown', this.handleDragStart);
    this.track.addEventListener('pointermove', this.handleDragMove);
    this.track.addEventListener('pointerup', this.handleDragEnd);
    this.track.addEventListener('pointercancel', this.handleDragEnd);
    this.track.addEventListener('scroll', this.handleScroll, { passive: true });

    // A drag that finishes on top of a link still fires a click, which would
    // navigate away the moment someone pushed the row along. Caught on the way
    // down so it is stopped before the link ever sees it.
    this.track.addEventListener(
      'click',
      (event) => {
        if (!this.moved) return;
        event.preventDefault();
        // Cleared here as well as on the next press, because a press that never
        // reaches this handler — a touch, which does not drag — would otherwise
        // inherit the flag from an earlier mouse drag and be swallowed too.
        this.moved = false;
      },
      true
    );

    document.addEventListener('visibilitychange', this.handleVisibility);

    // Open on the first real slide, with a full set waiting on either side.
    window.requestAnimationFrame(() => this.jumpTo(this.count));
    this.restart();
  }

  disconnectedCallback() {
    window.clearInterval(this.timer);
    window.clearTimeout(this.settle);
    document.removeEventListener('visibilitychange', this.handleVisibility);
  }

  /** A copy of the whole set either side of the real one. */
  buildLoop(originals) {
    const lead = document.createDocumentFragment();
    const trail = document.createDocumentFragment();

    originals.forEach((slide) => {
      [lead, trail].forEach((fragment) => {
        const copy = slide.cloneNode(true);
        copy.setAttribute('aria-hidden', 'true');
        fragment.append(copy);
      });
    });

    this.list.prepend(lead);
    this.list.append(trail);
  }

  handleVisibility() {
    if (document.hidden) window.clearInterval(this.timer);
    else this.restart();
  }

  restart() {
    window.clearInterval(this.timer);
    // Reduced motion means no unrequested movement at all, not faster movement.
    if (this.motion.matches) return;
    this.timer = window.setInterval(() => {
      // Always forward. The normalising jump keeps a slide in front of us.
      if (!this.paused && !this.dragging) this.goTo(this.nearestIndex() + 1);
    }, this.interval);
  }

  /**
   * Once the reader settles on a copy, move a whole set back to the matching
   * real slide. Instantly, and while nothing is animating.
   */
  handleScroll() {
    if (this.silent) return;
    window.clearTimeout(this.settle);
    this.settle = window.setTimeout(() => {
      if (this.dragging) return;
      const index = this.nearestIndex();
      if (index >= this.count * 2) this.jumpTo(index - this.count);
      else if (index < this.count) this.jumpTo(index + this.count);
    }, 150);
  }

  handleDragStart(event) {
    if (event.pointerType !== 'mouse') return;
    this.dragging = true;
    this.moved = false;
    this.originX = event.clientX;
    this.originScroll = this.track.scrollLeft;
  }

  handleDragMove(event) {
    if (!this.dragging) return;

    // Released somewhere else, where our own `pointerup` never fired. Without
    // this the track would keep following the cursor with no button held.
    if (event.buttons === 0) {
      this.dragging = false;
      return;
    }

    const travelled = event.clientX - this.originX;

    /**
     * Capture is taken here, on the first real movement — never on the press.
     *
     * While a pointer is captured the click is delivered to the capturing
     * element rather than to whatever sits under the cursor. Capturing on
     * pointerdown therefore swallowed the click of every card that is a link:
     * the anchor never saw it, and nothing ever opened. Waiting for three
     * pixels of travel draws the line between a click and a drag, and a plain
     * click now never captures at all.
     */
    if (!this.moved) {
      if (Math.abs(travelled) <= 3) return;
      this.moved = true;
      // Snapping fights a drag in progress, so it is suspended until release.
      this.track.classList.add('is-dragging');
      this.track.setPointerCapture(event.pointerId);
    }

    this.track.scrollLeft = this.originScroll - travelled;
  }

  handleDragEnd(event) {
    if (!this.dragging) return;
    this.dragging = false;
    this.track.classList.remove('is-dragging');
    if (this.track.hasPointerCapture(event.pointerId)) {
      this.track.releasePointerCapture(event.pointerId);
    }
    // Snap deliberately rather than waiting for the next scroll to trigger it.
    if (this.moved) this.goTo(this.nearestIndex());
    this.restart();
  }

  /** Whichever slide currently sits closest to the middle of the track. */
  nearestIndex() {
    const middle = this.track.getBoundingClientRect().left + this.track.clientWidth / 2;
    let closest = 0;
    let shortest = Infinity;

    this.slides.forEach((slide, index) => {
      const box = slide.getBoundingClientRect();
      const distance = Math.abs(box.left + box.width / 2 - middle);
      if (distance < shortest) {
        shortest = distance;
        closest = index;
      }
    });

    return closest;
  }

  /** The scroll position that puts a given slide in the middle of the track. */
  offsetFor(index) {
    const slide = this.slides[index];
    if (!slide) return null;

    const box = slide.getBoundingClientRect();
    return box.left
      - this.track.getBoundingClientRect().left
      + this.track.scrollLeft
      - (this.track.clientWidth - box.width) / 2;
  }

  goTo(index) {
    const left = this.offsetFor(index);
    if (left === null) return;
    this.track.scrollTo({ left, behavior: this.motion.matches ? 'auto' : 'smooth' });
  }

  jumpTo(index) {
    const left = this.offsetFor(index);
    if (left === null) return;
    this.silent = true;
    this.track.scrollTo({ left, behavior: 'auto' });
    window.requestAnimationFrame(() => {
      this.silent = false;
    });
  }
}

if (!customElements.get('lcl-slider')) customElements.define('lcl-slider', LclSlider);
